import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Box, 
  Toolbar, 
  Typography, 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton,
  ListItemIcon, 
  ListItemText, 
  IconButton, 
  Divider, 
  Container, 
  Avatar, 
  Menu, 
  MenuItem as MUIMenuItem 
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Security as SecurityIcon,
  ListAlt as ListAltIcon,
  Task as TaskIcon,
  Folder as FolderIcon,
  AccountCircle,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon,
  Gavel as GavelIcon,
  Analytics as AnalyticsIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  FolderShared as FolderSharedIcon,
  LibraryBooks as LibraryBooksIcon
} from '@mui/icons-material';
import { useAuth } from '../context/SimpleAuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { UserRole, MenuItem } from '../types/user';
import CompanySelector from './CompanySelector';
import { useCompany } from '../contexts/CompanyContext';

// Drawer width
const drawerWidth = 240;

/**
 * Props interface
 */
interface MaterialLayoutProps {
  children: React.ReactNode;
}

/**
 * Material Layout Component
 * 
 * Provides the main layout structure with navigation drawer and app bar
 */
export default function MaterialLayout({ children }: MaterialLayoutProps) {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { hasAnyRole, userRole } = usePermissions();
  const { currentCompany } = useCompany();

  // Define all menu items with role-based access
  const allMenuItems: MenuItem[] = [
    { 
      label: 'Dashboard', 
      path: '/', 
      icon: DashboardIcon, 
      allowedRoles: [UserRole.ALL] 
    },
    { 
      label: 'Executive Dashboard', 
      path: '/executive-dashboard', 
      icon: BusinessIcon, 
      allowedRoles: [UserRole.EXECUTIVE] 
    },
    { 
      label: 'Frameworks', 
      path: '/frameworks', 
      icon: DescriptionIcon, 
      allowedRoles: [
        UserRole.ADMIN, 
        UserRole.COMPLIANCE_OFFICER, 
        UserRole.RISK_SPECIALIST,
        UserRole.IMPLEMENTATION_OFFICER,
        UserRole.AUDITOR,
        UserRole.EXECUTIVE,
        UserRole.DOCUMENT_MANAGER,
        UserRole.EXTERNAL_CONSULTANT
      ] 
    },
    { 
      label: 'Obligations', 
      path: '/obligations', 
      icon: AssignmentIcon, 
      allowedRoles: [
        UserRole.ADMIN, 
        UserRole.COMPLIANCE_OFFICER, 
        UserRole.RISK_SPECIALIST,
        UserRole.IMPLEMENTATION_OFFICER,
        UserRole.AUDITOR,
        UserRole.DOCUMENT_MANAGER
      ] 
    },
    { 
      label: 'Controls', 
      path: '/controls', 
      icon: SecurityIcon, 
      allowedRoles: [
        UserRole.ADMIN, 
        UserRole.COMPLIANCE_OFFICER, 
        UserRole.RISK_SPECIALIST,
        UserRole.IMPLEMENTATION_OFFICER,
        UserRole.AUDITOR,
        UserRole.DOCUMENT_MANAGER
      ] 
    },
    { 
      label: 'Requirements', 
      path: '/requirements', 
      icon: ListAltIcon, 
      allowedRoles: [
        UserRole.ADMIN, 
        UserRole.COMPLIANCE_OFFICER, 
        UserRole.RISK_SPECIALIST,
        UserRole.IMPLEMENTATION_OFFICER,
        UserRole.AUDITOR,
        UserRole.DOCUMENT_MANAGER
      ] 
    },
    { 
      label: 'Tasks', 
      path: '/tasks', 
      icon: TaskIcon, 
      allowedRoles: [
        UserRole.ADMIN, 
        UserRole.COMPLIANCE_OFFICER, 
        UserRole.RISK_SPECIALIST,
        UserRole.IMPLEMENTATION_OFFICER,
        UserRole.DEPARTMENT_MANAGER,
        UserRole.AUDITOR,
        UserRole.DOCUMENT_MANAGER
      ] 
    },
    { 
      label: 'My Tasks', 
      path: '/my-tasks', 
      icon: TaskIcon, 
      allowedRoles: [UserRole.TASK_IMPLEMENTER] 
    },
    { 
      label: 'Documents', 
      path: '/documents', 
      icon: FolderIcon, 
      allowedRoles: [
        UserRole.ADMIN, 
        UserRole.COMPLIANCE_OFFICER, 
        UserRole.RISK_SPECIALIST,
        UserRole.IMPLEMENTATION_OFFICER,
        UserRole.DEPARTMENT_MANAGER,
        UserRole.TASK_IMPLEMENTER,
        UserRole.AUDITOR,
        UserRole.DOCUMENT_MANAGER,
        UserRole.EXTERNAL_CONSULTANT
      ] 
    },
    { 
      label: 'Templates', 
      path: '/templates', 
      icon: LibraryBooksIcon, 
      allowedRoles: [UserRole.DOCUMENT_MANAGER] 
    },
    { 
      label: 'Version Control', 
      path: '/version-control', 
      icon: FolderSharedIcon, 
      allowedRoles: [UserRole.DOCUMENT_MANAGER] 
    },
    { 
      label: 'Reports', 
      path: '/reports', 
      icon: AssessmentIcon, 
      allowedRoles: [
        UserRole.ADMIN,
        UserRole.COMPLIANCE_OFFICER,
        UserRole.DEPARTMENT_MANAGER,
        UserRole.EXECUTIVE,
        UserRole.AUDITOR
      ] 
    },
    { 
      label: 'Analytics', 
      path: '/analytics', 
      icon: AnalyticsIcon, 
      allowedRoles: [UserRole.EXECUTIVE] 
    },
    { 
      label: 'Audit Trail', 
      path: '/audit-trail', 
      icon: GavelIcon, 
      allowedRoles: [UserRole.AUDITOR] 
    },
    { 
      label: 'Team', 
      path: '/team', 
      icon: GroupIcon, 
      allowedRoles: [UserRole.DEPARTMENT_MANAGER] 
    },
    { 
      label: 'Users', 
      path: '/users', 
      icon: PeopleIcon, 
      allowedRoles: [UserRole.ADMIN] 
    },
    { 
      label: 'Settings', 
      path: '/settings', 
      icon: SettingsIcon, 
      allowedRoles: [UserRole.ADMIN] 
    },
    { 
      label: 'RBAC Management', 
      path: '/rbac', 
      icon: SecurityIcon, 
      allowedRoles: [UserRole.ADMIN] 
    },
    { 
      label: 'Assigned Frameworks', 
      path: '/assigned-frameworks', 
      icon: DescriptionIcon, 
      allowedRoles: [UserRole.EXTERNAL_CONSULTANT] 
    }
  ];

  // Filter menu items based on user role
  const visibleMenuItems = allMenuItems.filter(item => 
    hasAnyRole(item.allowedRoles)
  );

  /**
   * Handle drawer toggle
   */
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  /**
   * Handle profile menu open
   */
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Handle profile menu close
   */
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  /**
   * Handle logout
   */
  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
  };

  /**
   * Check if menu item is active
   */
  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  /**
   * Render drawer content
   */
  const drawerContent = (
    <>
      <Toolbar>
        <Typography variant="h6" noWrap>
          {currentCompany?.name || 'ModularCompliance'}
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {visibleMenuItems.map((item) => {
          const Icon = item.icon;
          return (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={isActive(item.path)}
                data-testid={`menu-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <ListItemIcon>{Icon && <Icon />}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      {/* User role indicator at bottom of drawer */}
      <Box sx={{ position: 'absolute', bottom: 0, width: '100%', p: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="caption" color="textSecondary" display="block" align="center">
          Role: {user?.role || 'Unknown'}
        </Typography>
      </Box>
    </>
  );

  /**
   * Render component
   */
  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          transition: (theme) => theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          ...(drawerOpen && {
            width: `calc(100% - ${drawerWidth}px)`,
            marginLeft: drawerWidth,
            transition: (theme) => theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }),
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="toggle drawer"
            onClick={toggleDrawer}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {visibleMenuItems.find(item => isActive(item.path))?.label || 'ModularCompliance'}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CompanySelector />
            <IconButton
              edge="end"
              color="inherit"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              data-testid="user-menu-button"
            >
              {user?.name ? (
                <Avatar sx={{ width: 32, height: 32 }}>
                  {user.name.charAt(0).toUpperCase()}
                </Avatar>
              ) : (
                <AccountCircle />
              )}
            </IconButton>
            
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleProfileMenuClose}
            >
              <MUIMenuItem disabled>
                <Typography variant="body2" color="textSecondary">
                  {user?.name || 'User'}
                </Typography>
              </MUIMenuItem>
              <MUIMenuItem disabled>
                <Typography variant="caption" color="textSecondary">
                  {user?.role || 'Unknown Role'}
                </Typography>
              </MUIMenuItem>
              <Divider />
              <MUIMenuItem onClick={handleLogout} data-testid="logout-button">Logout</MUIMenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            transition: (theme) => theme.transitions.create('transform', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            ...(drawerOpen ? {
              transform: 'translateX(0)',
            } : {
              transform: `translateX(-${drawerWidth}px)`,
            }),
          },
        }}
      >
        {drawerContent}
      </Drawer>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          padding: 3,
          transition: (theme) => theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          marginLeft: drawerOpen ? 0 : `-${drawerWidth}px`,
          width: drawerOpen ? `calc(100% - ${drawerWidth}px)` : '100%',
        }}
      >
        <Toolbar />
        <Container maxWidth="lg">
          {children}
        </Container>
      </Box>
    </Box>
  );
}
